// CB:BEGIN fr-034-admin-realtors-page
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../../lib/getApiUrl';
import { CheckCircle, AlertTriangle, XCircle, Clock, DollarSign, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import StatusBadge from '../../components/ui/StatusBadge';
import SharedHeaderAdmin from '../../components/SharedHeaderAdmin';

const BRAND = '#123c3b';

interface RealtorSummary {
  id: string;
  userId: string;
  email: string;
  brokerageName?: string | null;
  status: string;
  riskScore: number;
  abuseFlag: boolean;
  verifiedAt?: string | null;
  quoteCount: number;
  incentiveSummary: Record<string, number>;
  createdAt: string;
}

interface IncentiveLedgerRow {
  id: string;
  realtorUserId: string;
  projectId: string;
  status: string;
  amountCents: number;
  eligibleAt?: string | null;
  holdReleasesAt?: string | null;
  approvedAt?: string | null;
  paidAt?: string | null;
  clawedBackAt?: string | null;
  realtorUser?: { id: string; email: string; realtorProfile?: { brokerageName?: string | null; status: string } | null };
  project?: { id: string; address: string };
  dispute?: { id: string; status: string; reason: string } | null;
}

// All status badges use the shared StatusBadge component (imported above).
// ACTIVE, SENT, OPENED, CLAIMED, REVOKED, EXPIRED, INVITED etc. are defined in StatusBadge's STATUS_MAP.
// Local Badge alias for callsite brevity.
function Badge({ status }: { status: string }) {
  return <StatusBadge status={status} />;
}

interface DisputeRow {
  id: string;
  status: string;
  reason: string;
  createdAt: string;
  realtorUser?: { id: string; email: string } | null;
  ledger?: { id: string; status: string; amountCents: number; projectId: string } | null;
}

const AdminRealtorsPage: React.FC<{ onNavigate?: (screen: string) => void; initialTab?: 'list' | 'quotes' | 'portals' | 'incentives' | 'disputes' }> = ({ onNavigate, initialTab = 'list' }) => {
  const { getToken } = useAuth();
  const [view, setView] = useState<'list' | 'quotes' | 'portals' | 'incentives' | 'disputes'>(initialTab);
  const [realtors, setRealtors] = useState<RealtorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [quoteRequests, setQuoteRequests] = useState<any[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [portals, setPortals] = useState<any[]>([]);
  const [portalsLoading, setPortalsLoading] = useState(false);
  const [portalsFilter, setPortalsFilter] = useState<string>('');
  const [lineageModal, setLineageModal] = useState<any | null>(null);
  const [lineageLoading, setLineageLoading] = useState(false);
  const [incentives, setIncentives] = useState<IncentiveLedgerRow[]>([]);
  const [incentivesLoading, setIncentivesLoading] = useState(false);
  const [incentiveFilter, setIncentiveFilter] = useState('');
  const [disputes, setDisputes] = useState<DisputeRow[]>([]);
  const [disputesLoading, setDisputesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [reportModal, setReportModal] = useState<{ quoteId: string; address: string } | null>(null);
  const [reportUploading, setReportUploading] = useState(false);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const apiCall = useCallback(async (path: string, options?: RequestInit) => {
    const token = await getToken();
    const base = getApiBaseUrl().replace(/\/$/, '');
    const res = await fetch(`${base}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers ?? {}),
      },
    });
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data: json };
  }, [getToken]);

  const fetchRealtors = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data } = await apiCall('/api/admin/realtors');
      if (ok) setRealtors(data.realtors || []);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const fetchIncentives = useCallback(async (status?: string) => {
    setIncentivesLoading(true);
    try {
      const qs = status ? `?status=${status}` : '';
      const { ok, data } = await apiCall(`/api/admin/realtors/incentives${qs}`);
      if (ok) setIncentives(data.ledger || []);
    } finally {
      setIncentivesLoading(false);
    }
  }, [apiCall]);

  const fetchQuoteRequests = useCallback(async () => {
    setQuotesLoading(true);
    try {
      const { ok, data } = await apiCall('/api/admin/realtors/quote-requests');
      if (ok) setQuoteRequests(data.quoteRequests || []);
    } finally {
      setQuotesLoading(false);
    }
  }, [apiCall]);

  const fetchPortals = useCallback(async () => {
    setPortalsLoading(true);
    try {
      const { ok, data } = await apiCall('/api/admin/realtors/portal-invites');
      if (ok) setPortals(data.portalInvites || []);
    } finally {
      setPortalsLoading(false);
    }
  }, [apiCall]);

  const openLineage = async (inviteId: string) => {
    setLineageLoading(true);
    setLineageModal({ loading: true });
    try {
      const { ok, data } = await apiCall(`/api/admin/realtors/portal-invites/${inviteId}/lineage`);
      if (ok) setLineageModal(data);
      else setLineageModal({ error: data.error || 'Failed' });
    } finally {
      setLineageLoading(false);
    }
  };

  const revokeInvite = async (inviteId: string) => {
    if (!window.confirm('Revoke this portal invite?')) return;
    setActionLoading(`revoke-${inviteId}`);
    const { ok, data } = await apiCall(`/api/admin/realtors/portal-invites/${inviteId}/revoke`, { method: 'POST' });
    setActionLoading(null);
    if (ok) { showToast('Invite revoked.'); fetchPortals(); }
    else showToast(data.error || 'Failed', false);
  };

  const fetchDisputes = useCallback(async () => {
    setDisputesLoading(true);
    try {
      const { ok, data } = await apiCall('/api/admin/realtors/disputes');
      if (ok) setDisputes(data.disputes || []);
    } finally {
      setDisputesLoading(false);
    }
  }, [apiCall]);

  const export1099 = async () => {
    const year = new Date().getFullYear();
    const token = await getToken();
    const base = (window as any).__API_BASE__ || '';
    const url = `${base}/api/admin/realtors/tax/1099-export?year=${year}`;
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) { showToast('1099 export failed', false); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `1099-${year}.csv`;
    a.click();
  };

  const w9Action = async (userId: string, action: 'verify' | 'reject') => {
    if (action === 'reject') {
      const reason = window.prompt('Rejection reason (required):');
      if (!reason?.trim()) return;
      setActionLoading(`${userId}-w9-reject`);
      const { ok, data } = await apiCall(`/api/admin/realtors/${userId}/w9/reject`, { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) });
      setActionLoading(null);
      if (ok) { showToast('W-9 rejected.'); } else { showToast(data.error || 'Failed', false); }
      fetchRealtors();
    } else {
      setActionLoading(`${userId}-w9-verify`);
      const { ok, data } = await apiCall(`/api/admin/realtors/${userId}/w9/verify`, { method: 'POST' });
      setActionLoading(null);
      if (ok) { showToast('W-9 verified!'); } else { showToast(data.error || 'Failed', false); }
      fetchRealtors();
    }
  };

  const resolveDispute = async (disputeId: string) => {
    const decision = window.prompt('Decision / reasoning (required):');
    if (!decision?.trim()) return;
    const upheldStr = window.prompt('Uphold the dispute? (yes/no)');
    if (!upheldStr) return;
    const upheld = upheldStr.trim().toLowerCase().startsWith('y');
    setActionLoading(`dispute-${disputeId}`);
    const { ok, data } = await apiCall(`/api/admin/realtors/disputes/${disputeId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ decision: decision.trim(), upheld }),
    });
    setActionLoading(null);
    if (ok) { showToast(`Dispute ${upheld ? 'upheld' : 'denied'}.`); } else { showToast(data.error || 'Failed', false); }
    fetchDisputes();
  };

  useEffect(() => { fetchRealtors(); }, [fetchRealtors]);
  useEffect(() => { if (view === 'quotes') fetchQuoteRequests(); }, [view, fetchQuoteRequests]);
  useEffect(() => { if (view === 'portals') fetchPortals(); }, [view, fetchPortals]);
  useEffect(() => { if (view === 'incentives') fetchIncentives(incentiveFilter || undefined); }, [view, incentiveFilter, fetchIncentives]);
  useEffect(() => { if (view === 'disputes') fetchDisputes(); }, [view, fetchDisputes]);

  const realtorAction = async (userId: string, action: string, body?: object) => {
    const key = `${userId}-${action}`;
    setActionLoading(key);
    try {
      const { ok, data } = await apiCall(`/api/admin/realtors/${userId}/${action}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      if (ok) { showToast(`${action} succeeded.`); fetchRealtors(); }
      else showToast(data.error || `${action} failed.`, false);
    } finally {
      setActionLoading(null);
    }
  };

  const incentiveAction = async (ledgerId: string, action: string, body?: object) => {
    setActionLoading(`${ledgerId}-${action}`);
    try {
      const { ok, data } = await apiCall(`/api/admin/realtors/incentives/${ledgerId}/${action}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      if (ok) { showToast(`${action} succeeded.`); fetchIncentives(incentiveFilter || undefined); }
      else showToast(data.error || `${action} failed.`, false);
    } finally {
      setActionLoading(null);
    }
  };

  const promptAndDo = (action: string, cb: (reason: string) => void) => {
    const reason = window.prompt(`Reason for "${action}" (required):`);
    if (reason?.trim()) cb(reason.trim());
  };

  const handleReportUpload = async (file: File) => {
    if (!reportModal) return;
    if (file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
      showToast('Please upload a PDF file', false); return;
    }
    if (file.size > 50 * 1024 * 1024) { showToast('File must be under 50 MB', false); return; }
    setReportUploading(true);
    try {
      const token = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${base}/api/admin/realtors/quote-requests/${reportModal.quoteId}/upload-report`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const info = [
          data.roofAreaSqFt ? `${data.roofAreaSqFt} sq ft` : null,
          data.predominantPitch ? `pitch ${data.predominantPitch}` : null,
          data.redactionStatus === 'REDACTED' ? 'redacted ✓' : null,
        ].filter(Boolean).join(' · ');
        showToast(`Report uploaded${info ? ` — ${info}` : ''}`);
        setReportModal(null);
        fetchQuoteRequests();
      } else {
        showToast(data.error || 'Upload failed', false);
      }
    } catch {
      showToast('Upload failed', false);
    } finally {
      setReportUploading(false);
    }
  };

  return (
    <>
    <SharedHeaderAdmin onNavigate={onNavigate || (() => {})} />
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', padding: 24, minHeight: '100vh', background: '#f8fafc' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: 10,
          background: toast.ok ? '#dcfce7' : '#fee2e2', color: toast.ok ? '#166534' : '#991b1b', fontWeight: 600, fontSize: 14,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          {toast.msg}
        </div>
      )}

      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0 }}>Realtor Management</h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['list', 'quotes', 'portals', 'incentives', 'disputes'] as const).map((v) => {
              const label = v === 'list' ? 'Realtors' : v === 'quotes' ? 'Quote Requests' : v === 'portals' ? 'Portal Invites' : v === 'incentives' ? 'Incentive Queue' : 'Disputes';
              return (
                <button key={v} onClick={() => setView(v)} style={{
                  padding: '8px 18px', borderRadius: 8, border: '2px solid', cursor: 'pointer',
                  borderColor: view === v ? BRAND : '#e2e8f0',
                  background: view === v ? BRAND : '#fff',
                  color: view === v ? '#fff' : '#64748b',
                  fontWeight: 600, fontSize: 14,
                }}>
                  {label}
                </button>
              );
            })}
            <button onClick={export1099} style={{ padding: '8px 18px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Export 1099 CSV
            </button>
          </div>
        </div>

        {view === 'list' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button onClick={fetchRealtors} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Email', 'Brokerage', 'Status', 'W-9', 'Risk', 'Abuse', 'Quotes', 'Incentives', 'Actions'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {realtors.map((r: RealtorSummary & { w9Status?: string }) => (
                      <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 16px', fontSize: 14, color: '#0f172a' }}>{r.email}</td>
                        <td style={{ padding: '12px 16px', fontSize: 14, color: '#64748b' }}>{r.brokerageName || '—'}</td>
                        <td style={{ padding: '12px 16px' }}><Badge status={r.status} /></td>
                        <td style={{ padding: '12px 16px', fontSize: 13 }}>
                          {r.w9Status ? <Badge status={r.w9Status} /> : <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.riskScore}</td>
                        <td style={{ padding: '12px 16px' }}>{r.abuseFlag ? <AlertTriangle size={16} color="#ef4444" /> : '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.quoteCount}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13 }}>
                          {Object.entries(r.incentiveSummary).map(([s, c]) => (
                            <span key={s} style={{ marginRight: 6 }}>{s}: {c}</span>
                          ))}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {r.status === 'UNVERIFIED' && (
                              <button onClick={() => realtorAction(r.userId, 'verify')} disabled={!!actionLoading} style={actionBtn('#22c55e')}>
                                {actionLoading === `${r.userId}-verify` ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={12} />} Verify
                              </button>
                            )}
                            {r.w9Status === 'SUBMITTED_PENDING_REVIEW' && (
                              <>
                                <button onClick={() => w9Action(r.userId, 'verify')} disabled={!!actionLoading} style={actionBtn('#22c55e')}>
                                  {actionLoading === `${r.userId}-w9-verify` ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={12} />} Verify W-9
                                </button>
                                <button onClick={() => w9Action(r.userId, 'reject')} disabled={!!actionLoading} style={actionBtn('#ef4444')}>
                                  Reject W-9
                                </button>
                              </>
                            )}
                            {['UNVERIFIED', 'ACTIVE'].includes(r.status) && (
                              <button onClick={() => promptAndDo('suspend', (reason) => realtorAction(r.userId, 'suspend', { reason }))} disabled={!!actionLoading} style={actionBtn('#f59e0b')}>
                                Suspend
                              </button>
                            )}
                            {r.status === 'SUSPENDED' && (
                              <button onClick={() => realtorAction(r.userId, 'reinstate')} disabled={!!actionLoading} style={actionBtn('#3b82f6')}>
                                Reinstate
                              </button>
                            )}
                            {r.status !== 'TERMINATED' && (
                              <button onClick={() => promptAndDo('terminate', (reason) => realtorAction(r.userId, 'terminate', { reason }))} disabled={!!actionLoading} style={actionBtn('#ef4444')}>
                                <XCircle size={12} /> Terminate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {realtors.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No realtors found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {view === 'quotes' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button onClick={fetchQuoteRequests} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {quotesLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Realtor', 'Property Address', 'Status', 'Bids', 'Invites', 'Project', 'Report', 'Created'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {quoteRequests.map((q: any) => {
                      const invites: any[] = q.portalInvites ?? [];
                      const activeInvite = invites.find((i: any) => i.status === 'CLAIMED') ?? invites.find((i: any) => i.status === 'ACTIVE' || i.status === 'SENT' || i.status === 'OPENED') ?? invites[0];
                      const convertedProject = q.convertedProjects?.[0] ?? null;
                      return (
                        <tr key={q.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontSize: 14, color: '#0f172a' }}>
                            {q.realtor?.email ?? '—'}
                            {q.realtor?.realtorProfile?.brokerageName && (
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>{q.realtor.realtorProfile.brokerageName}</div>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 14, color: '#0f172a' }}>
                            {q.propertyAddressLine1}
                            {q.propertyCity ? `, ${q.propertyCity}` : ''}
                            {q.propertyState ? `, ${q.propertyState}` : ''}
                            {q.propertyZip ? ` ${q.propertyZip}` : ''}
                          </td>
                          <td style={{ padding: '12px 16px' }}><Badge status={q.status} /></td>
                          <td style={{ padding: '12px 16px', fontSize: 14 }}>{q.bids?.length ?? 0} / {q.maxBids ?? 3}</td>
                          <td style={{ padding: '12px 16px', fontSize: 13 }}>
                            {invites.length === 0 ? (
                              <span style={{ color: '#94a3b8' }}>None</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {activeInvite && <Badge status={activeInvite.status} />}
                                <span style={{ fontSize: 11, color: '#94a3b8' }}>{invites.length} invite{invites.length !== 1 ? 's' : ''}</span>
                                {activeInvite?.homeownerEmail && (
                                  <span style={{ fontSize: 11, color: '#64748b' }}>{activeInvite.homeownerEmail}</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13 }}>
                            {convertedProject ? (
                              <div>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', padding: '3px 8px', borderRadius: 999 }}>
                                  ✓ Project
                                </span>
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{convertedProject.address?.slice(0, 30)}</div>
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{convertedProject.status}</div>
                              </div>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {q.roofReport ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: '#166534', background: '#dcfce7', padding: '3px 8px', borderRadius: 999 }}>
                                  ✓ Report
                                </span>
                                {q.roofReport.roofAreaSqFt && (
                                  <span style={{ fontSize: 11, color: '#64748b' }}>{q.roofReport.roofAreaSqFt} sq ft</span>
                                )}
                              </div>
                            ) : (
                              <button onClick={() => setReportModal({ quoteId: q.id, address: q.propertyAddressLine1 })} style={actionBtn(BRAND)}>
                                + Add Report
                              </button>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>
                            {new Date(q.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })}
                    {quoteRequests.length === 0 && (
                      <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No quote requests found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* CB:BEGIN phase6-portal-invites-tab */}
        {view === 'portals' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              {['', 'ACTIVE', 'SENT', 'OPENED', 'CLAIMED', 'EXPIRED', 'REVOKED'].map((s) => (
                <button key={s} onClick={() => setPortalsFilter(s)} style={{
                  padding: '6px 13px', borderRadius: 8, border: '2px solid',
                  borderColor: portalsFilter === s ? BRAND : '#e2e8f0',
                  background: portalsFilter === s ? BRAND : '#fff',
                  color: portalsFilter === s ? '#fff' : '#64748b',
                  fontWeight: 600, fontSize: 12, cursor: 'pointer',
                }}>
                  {s || 'All'}
                </button>
              ))}
              <button onClick={() => setPortalsFilter('stuck')} style={{
                padding: '6px 13px', borderRadius: 8, border: '2px solid',
                borderColor: portalsFilter === 'stuck' ? '#dc2626' : '#fecaca',
                background: portalsFilter === 'stuck' ? '#fee2e2' : '#fff',
                color: portalsFilter === 'stuck' ? '#991b1b' : '#b91c1c',
                fontWeight: 600, fontSize: 12, cursor: 'pointer',
              }}>
                Stuck Only
              </button>
              <button onClick={fetchPortals} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 13px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 13, marginLeft: 'auto' }}>
                <RefreshCw size={13} /> Refresh
              </button>
            </div>
            {portalsLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (() => {
              const filtered = portals.filter((inv: any) => {
                if (!portalsFilter) return true;
                if (portalsFilter === 'stuck') return !!inv.stuckState;
                return inv.status === portalsFilter;
              });
              const STUCK_BG: Record<string, string> = {
                expired_unclaimed: '#fffbeb',
                sent_never_opened: '#fefce8',
                opened_not_claimed: '#fff7ed',
                claimed_no_project: '#fef2f2',
                created_not_sent: '#f0fdf4',
              };
              const STUCK_LABEL: Record<string, string> = {
                expired_unclaimed: 'Expired without claim',
                sent_never_opened: 'Sent — never opened',
                opened_not_claimed: 'Opened — not claimed',
                claimed_no_project: 'Claimed — no project!',
                created_not_sent: 'Created — not sent',
              };
              return (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        {['Status', 'Stuck?', 'Homeowner', 'Property', 'Realtor', 'Sent', 'Opened', 'Claimed', 'Expires', 'Project', 'Actions'].map((h) => (
                          <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((inv: any) => {
                        const q = inv.quoteRequest;
                        const project = q?.convertedProjects?.[0] ?? null;
                        const rowBg = inv.stuckState ? STUCK_BG[inv.stuckState] ?? '#fefce8' : undefined;
                        const now = new Date();
                        const isExpired = inv.expiresAt && new Date(inv.expiresAt) < now;
                        return (
                          <tr key={inv.id} style={{ borderTop: '1px solid #f1f5f9', background: rowBg }}>
                            <td style={{ padding: '11px 14px' }}><Badge status={inv.status} /></td>
                            <td style={{ padding: '11px 14px', fontSize: 12 }}>
                              {inv.stuckState ? (
                                <span style={{ color: '#dc2626', fontWeight: 600 }}>{STUCK_LABEL[inv.stuckState] ?? inv.stuckState}</span>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '11px 14px', fontSize: 13 }}>
                              <div style={{ fontWeight: 500 }}>{inv.homeownerName || '—'}</div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>{inv.homeownerEmail || '—'}</div>
                            </td>
                            <td style={{ padding: '11px 14px', fontSize: 13, color: '#374151', maxWidth: 180 }}>
                              {q ? `${q.propertyAddressLine1}, ${q.propertyCity ?? ''} ${q.propertyState ?? ''}`.trim() : '—'}
                            </td>
                            <td style={{ padding: '11px 14px', fontSize: 13 }}>
                              {q?.realtor?.email ?? '—'}
                              {q?.realtor?.realtorProfile?.brokerageName && (
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{q.realtor.realtorProfile.brokerageName}</div>
                              )}
                            </td>
                            <td style={{ padding: '11px 14px', fontSize: 12, color: '#64748b' }}>
                              {inv.sentAt ? new Date(inv.sentAt).toLocaleDateString() : <span style={{ color: '#94a3b8' }}>—</span>}
                            </td>
                            <td style={{ padding: '11px 14px', fontSize: 12, color: '#64748b' }}>
                              {inv.openedAt
                                ? new Date(inv.openedAt).toLocaleDateString()
                                : inv.previewHitCount > 0
                                ? <span style={{ color: '#f59e0b' }}>{inv.previewHitCount} hit{inv.previewHitCount !== 1 ? 's' : ''}</span>
                                : <span style={{ color: '#94a3b8' }}>—</span>
                              }
                            </td>
                            <td style={{ padding: '11px 14px', fontSize: 12, color: '#64748b' }}>
                              {inv.claimedAt ? new Date(inv.claimedAt).toLocaleDateString() : <span style={{ color: '#94a3b8' }}>—</span>}
                            </td>
                            <td style={{ padding: '11px 14px', fontSize: 12 }}>
                              <span style={{ color: isExpired ? '#dc2626' : '#64748b' }}>
                                {inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : '—'}
                              </span>
                            </td>
                            <td style={{ padding: '11px 14px', fontSize: 12 }}>
                              {project ? (
                                <div>
                                  <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', padding: '2px 7px', borderRadius: 999 }}>✓ {project.status}</span>
                                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{project.id.slice(0, 8)}…</div>
                                </div>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '11px 14px' }}>
                              <div style={{ display: 'flex', gap: 5 }}>
                                <button onClick={() => openLineage(inv.id)} style={actionBtn(BRAND)}>
                                  {lineageLoading ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                                  Diagnose
                                </button>
                                {!inv.claimedAt && !inv.revokedAt && inv.status !== 'EXPIRED' && (
                                  <button onClick={() => revokeInvite(inv.id)} disabled={!!actionLoading} style={actionBtn('#ef4444')}>
                                    {actionLoading === `revoke-${inv.id}` ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                                    Revoke
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filtered.length === 0 && (
                        <tr><td colSpan={11} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No portal invites{portalsFilter ? ` matching "${portalsFilter}"` : ''}.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}
        {/* CB:END phase6-portal-invites-tab */}

        {view === 'incentives' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {['', 'ELIGIBLE', 'APPROVED', 'PAID', 'CLAWED_BACK'].map((s) => (
                <button key={s} onClick={() => setIncentiveFilter(s)} style={{
                  padding: '7px 14px', borderRadius: 8, border: '2px solid',
                  borderColor: incentiveFilter === s ? BRAND : '#e2e8f0',
                  background: incentiveFilter === s ? BRAND : '#fff',
                  color: incentiveFilter === s ? '#fff' : '#64748b',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}>
                  {s || 'All'}
                </button>
              ))}
              <button onClick={() => fetchIncentives(incentiveFilter || undefined)} style={{ padding: '7px 14px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 13 }}>
                <RefreshCw size={13} style={{ display: 'inline', marginRight: 4 }} />Refresh
              </button>
            </div>
            {incentivesLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Realtor', 'Project', 'Amount', 'Status', 'Hold Releases', 'Dispute', 'Actions'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {incentives.map((row) => {
                      const holdOk = !row.holdReleasesAt || new Date() >= new Date(row.holdReleasesAt);
                      return (
                        <tr key={row.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontSize: 14 }}>
                            {row.realtorUser?.email}<br />
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>{row.realtorUser?.realtorProfile?.brokerageName}</span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{row.project?.address || row.projectId}</td>
                          <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600 }}>${(row.amountCents / 100).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px' }}><Badge status={row.status} /></td>
                          <td style={{ padding: '12px 16px', fontSize: 13 }}>
                            {row.holdReleasesAt ? (
                              holdOk
                                ? <span style={{ color: '#22c55e' }}><CheckCircle size={14} style={{ display: 'inline', marginRight: 4 }} />Elapsed</span>
                                : <span style={{ color: '#f59e0b' }}><Clock size={14} style={{ display: 'inline', marginRight: 4 }} />{new Date(row.holdReleasesAt).toLocaleDateString()}</span>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13 }}>
                            {row.dispute ? <Badge status={row.dispute.status} /> : '—'}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {row.status === 'ELIGIBLE' && holdOk && (
                                <button onClick={() => incentiveAction(row.id, 'approve')} disabled={!!actionLoading} style={actionBtn('#22c55e')}>
                                  <CheckCircle size={12} /> Approve
                                </button>
                              )}
                              {row.status === 'APPROVED' && (
                                <button onClick={() => {
                                  const method = window.prompt('Payout method (e.g. manual, stripe_connect):');
                                  if (method?.trim()) {
                                    const extId = window.prompt('External payout ID (optional):') || undefined;
                                    incentiveAction(row.id, 'record-payout', { payoutMethod: method.trim(), externalPayoutId: extId });
                                  }
                                }} disabled={!!actionLoading} style={actionBtn('#8b5cf6')}>
                                  <DollarSign size={12} /> Mark Paid
                                </button>
                              )}
                              {['ELIGIBLE', 'APPROVED'].includes(row.status) && (
                                <button onClick={() => promptAndDo('deny', (reason) => incentiveAction(row.id, 'deny', { reason }))} disabled={!!actionLoading} style={actionBtn('#94a3b8')}>
                                  Deny
                                </button>
                              )}
                              {['ELIGIBLE', 'APPROVED', 'PAID'].includes(row.status) && (
                                <button onClick={() => promptAndDo('clawback', (reason) => incentiveAction(row.id, 'clawback', { reason }))} disabled={!!actionLoading} style={actionBtn('#ef4444')}>
                                  Clawback
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {incentives.length === 0 && (
                      <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No incentive records.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

        {view === 'disputes' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button onClick={fetchDisputes} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {disputesLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Realtor', 'Incentive', 'Reason', 'Status', 'Opened', 'Actions'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {disputes.map((d) => (
                      <tr key={d.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 16px', fontSize: 14 }}>{d.realtorUser?.email || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>
                          ${((d.ledger?.amountCents ?? 0) / 100).toFixed(2)}<br />
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>{d.ledger?.projectId?.slice(0, 8)}…</span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, maxWidth: 240 }}>{d.reason}</td>
                        <td style={{ padding: '12px 16px' }}><Badge status={d.status} /></td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{new Date(d.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding: '12px 16px' }}>
                          {d.status === 'OPEN' && (
                            <button onClick={() => resolveDispute(d.id)} disabled={!!actionLoading} style={actionBtn(BRAND)}>
                              {actionLoading === `dispute-${d.id}` ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                              Resolve
                            </button>
                          )}
                          {d.status !== 'OPEN' && <span style={{ fontSize: 12, color: '#94a3b8' }}>Resolved</span>}
                        </td>
                      </tr>
                    ))}
                    {disputes.length === 0 && (
                      <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No disputes found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CB:BEGIN phase6-lineage-modal */}
      {lineageModal && (
        <div onClick={() => setLineageModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 32, width: '100%', maxWidth: 680, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>Invite Lineage Drill-Down</h2>
              <button onClick={() => setLineageModal(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
            {lineageModal.loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : lineageModal.error ? (
              <p style={{ color: '#dc2626' }}>{lineageModal.error}</p>
            ) : (() => {
              const inv = lineageModal.invite;
              const diag = lineageModal.diagnostics ?? {};
              const project = lineageModal.project;
              const qr = inv?.quoteRequest;
              const DIAG_OK = '#dcfce7'; const DIAG_WARN = '#fef9c3'; const DIAG_ERR = '#fee2e2';
              const checkRow = (label: string, ok: boolean | null, detail?: string) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: ok === null ? DIAG_WARN : ok ? DIAG_OK : DIAG_ERR, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: ok === null ? '#854d0e' : ok ? '#166534' : '#991b1b', flexShrink: 0 }}>
                    {ok === null ? '?' : ok ? '✓' : '✗'}
                  </span>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
                    {detail && <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>{detail}</span>}
                  </div>
                </div>
              );
              return (
                <>
                  {diag.stuckState && (
                    <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 14, color: '#991b1b', fontWeight: 600 }}>
                      Stuck: {({ expired_unclaimed: 'Expired without claim', sent_never_opened: 'Sent but never opened', opened_not_claimed: 'Homeowner opened but did not claim', claimed_no_project: 'Marked claimed but no project was created', created_not_sent: 'Created but email never sent' } as any)[diag.stuckState] ?? diag.stuckState}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Invite</div>
                      <div style={{ fontSize: 13 }}><strong>Status:</strong> <Badge status={inv?.status ?? '?'} /></div>
                      <div style={{ fontSize: 13, marginTop: 4 }}><strong>Homeowner:</strong> {inv?.homeownerEmail || '—'}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}><strong>Sent:</strong> {inv?.sentAt ? new Date(inv.sentAt).toLocaleString() : '—'}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}><strong>Opened:</strong> {inv?.openedAt ? new Date(inv.openedAt).toLocaleString() : `${inv?.previewHitCount ?? 0} preview hit(s)`}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}><strong>Claimed:</strong> {inv?.claimedAt ? new Date(inv.claimedAt).toLocaleString() : '—'}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}><strong>Expires:</strong> {inv?.expiresAt ? new Date(inv.expiresAt).toLocaleString() : '—'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Quote Request</div>
                      <div style={{ fontSize: 13 }}><strong>Status:</strong> <Badge status={qr?.status ?? '?'} /></div>
                      <div style={{ fontSize: 13, marginTop: 4 }}><strong>Realtor:</strong> {qr?.realtor?.email ?? '—'}</div>
                      {qr?.realtor?.realtorProfile?.brokerageName && <div style={{ fontSize: 12, color: '#64748b' }}>{qr.realtor.realtorProfile.brokerageName}</div>}
                      <div style={{ fontSize: 13, marginTop: 4 }}><strong>Bids:</strong> {qr?.bids?.length ?? 0}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}><strong>Address:</strong> {qr ? `${qr.propertyAddressLine1}, ${qr.propertyCity ?? ''} ${qr.propertyState ?? ''}` : '—'}</div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Diagnostic Checklist</div>
                    {checkRow('Invite sent', inv?.sentAt ? true : null, inv?.sentAt ? 'Email dispatched' : 'sentAt not set')}
                    {checkRow('Homeowner opened/previewed', (inv?.openedAt || (inv?.previewHitCount ?? 0) > 0) ? true : (diag.isExpired ? false : null))}
                    {checkRow('Invite claimed', !!inv?.claimedAt)}
                    {checkRow('Project created', diag.hasProject, project ? `${project.status} · ${project.id?.slice(0, 8)}…` : undefined)}
                    {diag.hasProject && checkRow('Bids attached to project', diag.hasBids, `${project?.bids?.length ?? 0} bid(s)`)}
                    {diag.hasProject && checkRow('Files / documents present', diag.hasFiles, `${lineageModal.projectDocs?.length ?? 0} doc(s)`)}
                    {diag.hasProject && checkRow('Attribution recorded', diag.hasAttribution)}
                  </div>
                  {project && (
                    <div style={{ padding: '12px 16px', background: '#f0fdf4', borderRadius: 8, fontSize: 13 }}>
                      <strong>Resulting project:</strong> {project.address} · <Badge status={project.status} />
                      {lineageModal.claimedByUser && (
                        <span style={{ marginLeft: 12, color: '#64748b' }}>Claimed by: {lineageModal.claimedByUser.email}</span>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
      {/* CB:END phase6-lineage-modal */}

      {/* Report upload modal */}
      {reportModal && (
        <div
          onClick={() => !reportUploading && setReportModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 32, width: '100%', maxWidth: 520, boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}
          >
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Upload Roof Report</h2>
            <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 20px' }}>
              Property: <strong>{reportModal.address}</strong>
            </p>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px', lineHeight: 1.5 }}>
              Upload the EagleView or similar PDF. It will be automatically redacted and parsed — sq footage, pitch, and other metrics will be extracted and made available to contractors when bidding.
            </p>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 14, color: '#374151', marginBottom: 10 }}>
              PDF File (max 50 MB)
            </label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              disabled={reportUploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleReportUpload(f);
              }}
              style={{ display: 'block', marginBottom: 20, fontSize: 14 }}
            />
            {reportUploading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#3b82f6', fontSize: 14, marginBottom: 16 }}>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Uploading and parsing… this may take a moment
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setReportModal(null)}
                disabled={reportUploading}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

function actionBtn(color: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '5px 10px', borderRadius: 6, border: `1.5px solid ${color}`,
    background: '#fff', color: color, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  };
}

export default AdminRealtorsPage;
// CB:END fr-034-admin-realtors-page
